import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { PageHeader, SectionCard } from "@/components/common";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Store, Plus, Trash2, Wand2, Rocket, Loader2, Gem, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const emptyRow = () => ({ uid: Math.random().toString(36).slice(2), name: "", category: "General", quantity: 10, purchase_price: "", selling_price: "" });

function margin(cost, sell) {
  const c = +cost, s = +sell;
  if (!s || s <= 0) return null;
  return Math.round(((s - c) / s) * 100);
}

export default function OpenShop() {
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  const [shopName, setShopName] = useState("");
  const [industry, setIndustry] = useState("retail");
  const [targetMargin, setTargetMargin] = useState(30);
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/business").then((r) => {
      setBiz(r.data);
      setShopName(r.data.name || "");
      if (r.data.industries?.length) setIndustry(r.data.industries[0]);
    }).catch(() => {});
  }, []);

  const update = (i, k, v) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows((rs) => [...rs, emptyRow()]);
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const applyMargin = () => {
    const m = +targetMargin;
    if (targetMargin === "" || isNaN(m)) return toast.error("Enter a margin %");
    if (m < 0 || m >= 100) return toast.error("Margin must be between 0 and 99%");
    setRows((rs) => rs.map((r) => {
      const cost = +r.purchase_price;
      if (!cost || cost <= 0) return r;
      // selling price so that margin% of selling = profit  => sell = cost / (1 - m/100)
      const sell = Math.round(cost / (1 - m / 100));
      return { ...r, selling_price: sell };
    }));
    toast.success(`Applied ${m}% margin to all priced items`);
  };

  const validRows = rows.filter((r) => r.name.trim() && +r.selling_price > 0);
  const totalCost = validRows.reduce((s, r) => s + (+r.purchase_price || 0) * (+r.quantity || 0), 0);
  const potentialRevenue = validRows.reduce((s, r) => s + (+r.selling_price || 0) * (+r.quantity || 0), 0);
  const avgMargin = validRows.length
    ? Math.round(validRows.reduce((s, r) => s + (margin(r.purchase_price, r.selling_price) || 0), 0) / validRows.length)
    : 0;

  const openShop = async () => {
    if (validRows.length === 0) return toast.error("Add at least one item with a name and selling price");
    setSaving(true);
    try {
      const items = validRows.map((r) => ({
        name: r.name.trim(), category: r.category || "General",
        quantity: +r.quantity || 0, purchase_price: +r.purchase_price || 0,
        selling_price: +r.selling_price || 0, industry,
      }));
      await api.post("/products/bulk", { items });
      await api.post("/business/open", { business_name: shopName, industries: [industry] });
      toast.success(`Shop is open! ${items.length} items added and ready to sell.`);
      navigate("/inventory");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open shop");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Open Shop" subtitle="Set up your shop and price your whole catalog in one go.">
        <Button variant="outline" onClick={() => navigate("/inventory")} data-testid="skip-to-inventory">Go to Inventory</Button>
      </PageHeader>

      {/* Shop details */}
      <SectionCard className="mb-6" testid="shop-details">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label>Shop name</Label>
            <Input data-testid="shop-name" value={shopName} onChange={(e) => setShopName(e.target.value)} placeholder="e.g. Khevora Emporium" className="mt-1" />
          </div>
          <div>
            <Label>Shop type</Label>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger data-testid="shop-industry" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="retail">Retail Store</SelectItem>
                <SelectItem value="jewellery">Jewellery Store</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </SectionCard>

      {/* Margin tool */}
      <div className="mb-6 rounded-xl border border-primary/30 bg-primary/5 p-5 flex flex-col sm:flex-row sm:items-center gap-4 animate-fade-up" data-testid="margin-tool">
        <div className="grid place-items-center h-11 w-11 rounded-lg bg-primary text-primary-foreground shrink-0">
          <Wand2 className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="font-heading font-semibold">Price everything with one margin</div>
          <p className="text-sm text-muted-foreground">Enter your cost prices below, set a target profit margin, and we'll set the selling price for every item.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Input data-testid="target-margin" type="number" value={targetMargin} onChange={(e) => setTargetMargin(e.target.value)} className="w-24 pr-7" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
          </div>
          <Button onClick={applyMargin} data-testid="apply-margin-btn" className="font-semibold whitespace-nowrap">Apply to all</Button>
        </div>
      </div>

      {/* Items table */}
      <SectionCard title="Your Items" subtitle="Add every product you want to sell" testid="items-table"
        action={<Button variant="outline" size="sm" onClick={addRow} data-testid="add-row-btn"><Plus className="h-4 w-4 mr-1.5" /> Add item</Button>}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left font-semibold py-2.5 min-w-[180px]">Item name</th>
                <th className="text-left font-semibold py-2.5 hidden sm:table-cell">Category</th>
                <th className="text-right font-semibold py-2.5">Qty</th>
                <th className="text-right font-semibold py-2.5">Cost ₹</th>
                <th className="text-right font-semibold py-2.5">Sell ₹</th>
                <th className="text-right font-semibold py-2.5">Margin</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const m = margin(r.purchase_price, r.selling_price);
                return (
                  <tr key={r.uid} className="border-b border-border/60">
                    <td className="py-2 pr-2">
                      <Input data-testid={`row-name-${i}`} value={r.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Product name" className="h-9" />
                    </td>
                    <td className="py-2 pr-2 hidden sm:table-cell">
                      <Input data-testid={`row-category-${i}`} value={r.category} onChange={(e) => update(i, "category", e.target.value)} className="h-9 w-28" />
                    </td>
                    <td className="py-2 pr-2">
                      <Input data-testid={`row-qty-${i}`} type="number" value={r.quantity} onChange={(e) => update(i, "quantity", e.target.value)} className="h-9 w-20 text-right" />
                    </td>
                    <td className="py-2 pr-2">
                      <Input data-testid={`row-cost-${i}`} type="number" value={r.purchase_price} onChange={(e) => update(i, "purchase_price", e.target.value)} placeholder="0" className="h-9 w-24 text-right" />
                    </td>
                    <td className="py-2 pr-2">
                      <Input data-testid={`row-sell-${i}`} type="number" value={r.selling_price} onChange={(e) => update(i, "selling_price", e.target.value)} placeholder="0" className="h-9 w-24 text-right" />
                    </td>
                    <td className="py-2 pr-2 text-right">
                      {m === null ? <span className="text-muted-foreground">—</span> : (
                        <span className={`font-semibold tabular-nums ${m < 0 ? "text-destructive" : m < 15 ? "text-[hsl(var(--warning))]" : "text-[hsl(var(--success))]"}`}>{m}%</span>
                      )}
                    </td>
                    <td className="py-2 text-right">
                      <button onClick={() => removeRow(i)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`remove-row-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Summary + open */}
      <div className="mt-6 grid md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-border bg-card p-4"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Items</div><div className="font-heading text-xl font-bold mt-1">{validRows.length}</div></div>
        <div className="rounded-xl border border-border bg-card p-4"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Stock Value (cost)</div><div className="font-heading text-xl font-bold mt-1">{formatINR(totalCost, { compact: true })}</div></div>
        <div className="rounded-xl border border-border bg-card p-4"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Potential Revenue</div><div className="font-heading text-xl font-bold mt-1 text-[hsl(var(--success))]">{formatINR(potentialRevenue, { compact: true })}</div></div>
        <div className="rounded-xl border border-border bg-card p-4"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Avg Margin</div><div className="font-heading text-xl font-bold mt-1 text-primary">{avgMargin}%</div></div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={openShop} disabled={saving || validRows.length === 0} data-testid="open-shop-btn" size="lg" className="font-semibold">
          {saving ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Rocket className="h-5 w-5 mr-2" />}
          Open Shop & Add {validRows.length || ""} Items
        </Button>
      </div>
    </div>
  );
}
